"use client"

import { useTranslation } from "@/i18n/client"
import { localizePathname } from "@/lib/locale-routing"

interface FreeEsimSectionProps {
  lng: string
  countryName?: string
}

export default function FreeEsimSection({ lng, countryName }: FreeEsimSectionProps) {
  const { t, ready } = useTranslation(lng, "product")

  if (!ready) return null

  return (
    <section id="free-esim" className="bg-white">
      <div className="mx-auto max-w-[1200px] px-5 py-8 md:px-10 md:py-10">
        <div
          className="rounded-3xl border border-stone-200 bg-white p-5 md:p-6"
          style={{
            boxShadow:
              "0 16px 40px -12px rgba(28,25,23,0.18), 0 4px 12px -4px rgba(28,25,23,0.08)",
          }}
        >
          <div className="mb-5 flex items-center gap-3">
            <span
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"
              style={{ backgroundColor: "rgba(234,88,12,0.10)" }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ea580c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <ellipse cx="12" cy="5" rx="9" ry="3" />
                <path d="M3 5V19A9 3 0 0 0 21 19V5" />
                <path d="M3 12A9 3 0 0 0 21 12" />
              </svg>
            </span>
            <div>
              <p className="text-base font-semibold text-stone-900">
                {t("free-esim-banner-title", { name: countryName || "" })}
              </p>
              <p className="mt-0.5 text-xs leading-relaxed text-stone-500">
                {t("free-esim-banner-desc")}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {/* Path A — Free */}
            <div className="flex flex-col rounded-2xl border border-stone-200/70 bg-white p-4">
              <div className="mb-3 flex items-center gap-2.5">
                <span
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"
                  style={{ backgroundColor: "rgba(234,88,12,0.10)" }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ea580c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 12 20 22 4 22 4 12" />
                    <rect width="20" height="5" x="2" y="7" />
                    <line x1="12" x2="12" y1="22" y2="7" />
                    <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
                    <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
                  </svg>
                </span>
                <p className="text-base font-semibold text-stone-900">
                  {t("free-esim-banner-free-title")}
                </p>
              </div>
              <ul className="mb-4 space-y-1.5 text-[13px] leading-relaxed text-stone-600">
                {(["free-esim-banner-free-item1", "free-esim-banner-free-item2", "free-esim-banner-free-item3"] as const).map((key) => (
                  <li key={key} className="flex items-start gap-2">
                    <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-orange-300" />
                    <span>{t(key)}</span>
                  </li>
                ))}
              </ul>
              <a
                href={localizePathname("/freeesim", lng)}
                data-track="click_free_esim_cta"
                data-track-section="free-esim"
                className="mt-auto inline-flex h-11 items-center justify-center rounded-full border border-orange-200 bg-white px-5 text-sm font-semibold text-orange-700 transition-colors hover:border-orange-300 hover:bg-orange-50"
              >
                {t("free-esim-banner-cta")}
              </a>
            </div>

            {/* Path B — Paid */}
            <div className="flex flex-col rounded-2xl border border-orange-200/60 bg-orange-50/40 p-4">
              <div className="mb-3 flex items-center gap-2.5">
                <span
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-home-orange_1"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m12 14 4-4" />
                    <path d="M3.34 19a10 10 0 1 1 17.32 0" />
                  </svg>
                </span>
                <p className="text-base font-semibold text-stone-900">
                  {t("free-esim-banner-paid-title")}
                </p>
              </div>
              <ul className="mb-4 space-y-1.5 text-[13px] leading-relaxed text-stone-600">
                {(["free-esim-banner-paid-item1", "free-esim-banner-paid-item2", "free-esim-banner-paid-item3"] as const).map((key) => (
                  <li key={key} className="flex items-start gap-2">
                    <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-home-orange_1" />
                    <span>{t(key)}</span>
                  </li>
                ))}
              </ul>
              <a
                href="#configurator"
                data-track="click_continue_paid"
                data-track-section="free-esim"
                className="mt-auto inline-flex h-11 items-center justify-center rounded-full bg-home-orange_1 px-5 text-sm font-semibold text-white transition-colors hover:bg-orange-700"
                style={{ boxShadow: "0 8px 18px -8px rgba(234,88,12,0.55)" }}
              >
                {t("free-esim-banner-skip")}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
